import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import type { QueryTypes } from '@seen/core/search'
import { useMemo } from 'react'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

export interface OrganizationsQueryResult {
  organizations: ExtendedOrganization[]
  isPending: boolean
  isError: boolean
}

export function useOrganizations(): ExtendedOrganization[] {
  return useSortedOrganizations(ExtendedOrganization.searchQuery())
    .organizations
}

// Non-excluding variant for the Send Fax recipient list, where non-network
// organizations must stay selectable. Clinician-facing callers keep
// useOrganizations, which hides non-network entries.
export function useFaxRecipientOrganizations(): ExtendedOrganization[] {
  return useSortedOrganizations(ExtendedOrganization.faxRecipientSearchQuery())
    .organizations
}

// Every organization plus the read's status, for callers deriving a contract:
// a non-network organization can carry one, so the excluding query would hide
// it, and an unsettled or failed read must not be mistaken for "no contract".
export function useAllOrganizationsQuery(): OrganizationsQueryResult {
  return useSortedOrganizations(ExtendedOrganization.faxRecipientSearchQuery())
}

function useSortedOrganizations(query: QueryTypes): OrganizationsQueryResult {
  const {
    data: organizations,
    isPending,
    isError,
  } = useSearchResourcePages(ExtendedOrganization, query)

  const sorted = useMemo(() => {
    if (!organizations) {
      return EMPTY_ORGANIZATIONS
    }

    // Preferred above available above the rest; ties keep server order.
    return [...organizations].sort(
      (a, b) => b.availabilityRank - a.availabilityRank
    )
  }, [organizations])

  return { organizations: sorted, isPending, isError }
}

const EMPTY_ORGANIZATIONS: ExtendedOrganization[] = []
